import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab from '@mui/joy/Tab';
import TabPanel from '@mui/joy/TabPanel';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import { Plus as AddRounded, Robot as SmartToyOutlined, Ticket as ConfirmationNumberOutlined } from '@phosphor-icons/react';
import type { AIChatMessage } from '../types/support';
import { useActiveAIConversation } from '../hooks/useSupport';
import AIChatPanel from '../components/support/AIChatPanel';
import TicketList from '../components/support/TicketList';
import TicketDetail from '../components/support/TicketDetail';
import EscalateToTicketDialog from '../components/support/EscalateToTicketDialog';
import { PageHelpButton } from '../components/knowledge-base/PageHelpButton';

const WELCOME_MESSAGE: AIChatMessage = {
  role: 'assistant',
  content: "Hi! I'm the ApexOS support assistant. I can help you with questions about any feature in the platform — from CRM and billing to marketing and scheduling. What can I help you with today?",
};

// Height of the scrollable panel (chat / ticket detail). Bounded so the header
// and tabs stay put and only the panel content scrolls — on both desktop and
// mobile (dvh accounts for mobile browser chrome).
const PANEL_HEIGHT = { xs: 'calc(100dvh - 300px)', md: 'min(70vh, 640px)' } as const;

export default function SupportPage() {
  const { ticketId: urlTicketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(urlTicketId ?? null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [aiContext, setAIContext] = useState<AIChatMessage[]>([]);
  const [activeTab, setActiveTab] = useState<number>(urlTicketId ? 1 : 0);

  // AI chat state for the full page
  const [messages, setMessages] = useState<AIChatMessage[]>([WELCOME_MESSAGE]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { data: activeConversation } = useActiveAIConversation();

  useEffect(() => {
    if (activeConversation?.messages && activeConversation.messages.length > 0) {
      setMessages([WELCOME_MESSAGE, ...activeConversation.messages]);
      setConversationId(activeConversation.id);
    }
  }, [activeConversation?.id]);

  const handleEscalate = (context: AIChatMessage[]) => {
    setAIContext(context);
    setEscalateOpen(true);
  };

  const handleTicketCreated = (ticketId: string) => {
    setEscalateOpen(false);
    setSelectedTicketId(ticketId);
    setActiveTab(1);
    navigate(`/support/${ticketId}`, { replace: true });
  };

  const handleSelectTicket = (ticket: { id: string }) => {
    setSelectedTicketId(ticket.id);
    navigate(`/support/${ticket.id}`, { replace: true });
  };

  const handleBackToList = () => {
    setSelectedTicketId(null);
    navigate('/support', { replace: true });
  };

  return (
    <Box sx={{ width: '95%', maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2.5,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h2">Support</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Get help, ask the AI assistant, or view your tickets
          </Typography>
        </Box>
        <Button
          variant="solid"
          startDecorator={<AddRounded />}
          onClick={() => { setAIContext([]); setEscalateOpen(true); }}
          sx={{ flexShrink: 0 }}
        >
          New Ticket
        </Button>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v as number);
          if (v === 0) handleBackToList();
        }}
      >
        <TabList sx={{ mb: 2 }}>
          <Tab value={0}>
            <SmartToyOutlined size={18} /> Ask AI Assistant
          </Tab>
          <Tab value={1}>
            <ConfirmationNumberOutlined size={18} /> My Tickets
          </Tab>
        </TabList>

        {/* Ask AI Assistant */}
        <TabPanel value={0} sx={{ p: 0 }}>
          <Card
            variant="outlined"
            sx={{
              height: PANEL_HEIGHT,
              minHeight: 380,
              p: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                p: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&:last-child': { pb: 0 },
              }}
            >
              <AIChatPanel
                messages={messages}
                setMessages={setMessages}
                conversationId={conversationId}
                setConversationId={setConversationId}
                onEscalate={handleEscalate}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* My Tickets */}
        <TabPanel value={1} sx={{ p: 0 }}>
          {selectedTicketId ? (
            <Card
              variant="outlined"
              sx={{ height: PANEL_HEIGHT, minHeight: 380, p: 0, overflow: 'auto' }}
            >
              <TicketDetail ticketId={selectedTicketId} onBack={handleBackToList} />
            </Card>
          ) : (
            <Card
              variant="outlined"
              sx={{ height: PANEL_HEIGHT, minHeight: 380, p: 0, overflow: 'auto' }}
            >
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <TicketList onSelectTicket={handleSelectTicket} selectedTicketId={selectedTicketId} />
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </Tabs>

      <EscalateToTicketDialog
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        aiContext={aiContext}
        onSuccess={handleTicketCreated}
      />
    </Box>
  );
}
